import { useCallback, useEffect } from "react";
import {
  HydratedOpportunity,
  OPPORTUNITY_STATUS_DISPLAY_NAMES,
} from "@forgepoint/shared";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { EntitySelector } from "@/components/EntitySelector";

interface OpportunitySelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (opportunityId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: "default" | "ghost";
  excludeOpportunityId?: string;
}

/**
 * Opportunity selector component that provides a searchable dropdown for selecting opportunities.
 * Uses the generic EntitySelector with opportunity-specific configuration.
 */
export function OpportunitySelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select an opportunity...",
  className,
  disabled = false,
  variant = "default",
  excludeOpportunityId,
}: OpportunitySelectorProps) {
  const { opportunities, listLoading, fetchOpportunities, getOpportunity } =
    useOpportunitiesStore();

  // Ensure selected opportunity is in cache when selection changes
  useEffect(() => {
    if (currentlySelectedId && !opportunities.has(currentlySelectedId)) {
      getOpportunity(currentlySelectedId);
    }
  }, [currentlySelectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEntities = useCallback(
    async (search?: string) => {
      const result = await fetchOpportunities(search);
      return result.ids;
    },
    [fetchOpportunities],
  );

  const getDisplayName = useCallback((opportunity: HydratedOpportunity) => {
    return opportunity.name;
  }, []);

  const getSecondaryInfo = useCallback((opportunity: HydratedOpportunity) => {
    const parts = [];
    const statusDisplay = OPPORTUNITY_STATUS_DISPLAY_NAMES[opportunity.status];
    parts.push(statusDisplay);
    if (opportunity.amount) {
      parts.push(`$${parseFloat(opportunity.amount).toLocaleString()}`);
    }
    if (opportunity.company?.name) {
      parts.push(opportunity.company.name);
    }
    return parts.join(" • ");
  }, []);

  return (
    <EntitySelector<HydratedOpportunity>
      entities={opportunities}
      listLoading={listLoading}
      fetchEntities={fetchEntities}
      getDisplayName={getDisplayName}
      getSecondaryInfo={getSecondaryInfo}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
      placeholder={placeholder}
      searchPlaceholder="Search opportunities..."
      className={className}
      disabled={disabled}
      variant={variant}
      excludeId={excludeOpportunityId}
      entityLabel="opportunities"
    />
  );
}
